import { type ReactNode } from 'react';

import { type HandoverSpec } from '@/designs-dev/handover/types';
import { layer, type VersionedSpec } from '@/designs-dev/handover/versions';
import { intakeProfileReviewSpec } from '@/karehero-app/handover/intake-profile-review';
import { onboarding11Spec } from '@/karehero-app/handover/onboarding-1-1';
import { onboarding20Spec } from '@/karehero-app/handover/onboarding-2-0';
import { onboarding21Spec } from '@/karehero-app/handover/onboarding-2-1';
import { patternsSpec } from '@/karehero-app/handover/patterns';
import { patterns20Spec } from '@/karehero-app/handover/patterns-2-0';
import { patterns41Spec } from '@/karehero-app/handover/patterns-4-1';
import { FlowVersionLayer } from '@/karehero-app/tell-us-once/intake/flowVersion';

// Tell Us Once's onboarding, every version on one page. See versions.ts.
//
// To start a new minor: add a version here layered on the one before, with its
// sections and its changes. To start a new major: its spec becomes the base the
// next minors layer on, and the old major stays in this list to switch back to.

const [happy21, ...changed21] = onboarding21Spec.flows;

// Every 4.0 screen drawn in 4.1's words.
const as21 = (page: ReactNode) => (
  <FlowVersionLayer.Provider value={{ from: '2.0-dev', to: '2.1' }}>
    {page}
  </FlowVersionLayer.Provider>
);

// The A/B test's sections, which live on a page of their own rather than among
// the screens: 4.0's option B, and 4.1's way out to the form.
const OPTION_B = 'Option B, for A/B testing';
const WAY_OUT = 'IDEA: a way out, for A/B testing the form';

const section = (spec: HandoverSpec, title: string) => {
  const found = spec.flows.find((f) => f.title === title);
  if (!found) throw new Error(`No section called "${title}" in ${spec.id}`);
  return found;
};

// The screens read left to right without arrows between them, every section.
const noArrows = (spec: HandoverSpec): HandoverSpec => ({
  ...spec,
  flows: spec.flows.map((f) => ({ ...f, arrows: false })),
});

// Sections of 4.0 that 4.1 has no place for: they belong to the AI consent the
// opening no longer asks. 4.1's own version of the missing-need path replaces
// 4.0's and arrives with the other changed sections.
const GONE_IN_2_1 = [
  "If they didn't say what they need help with",
  'Reading the AI policy',
  'No AI consent',
];

export const onboardingVersions: VersionedSpec = {
  id: 'onboarding-chat',
  title: 'Onboarding',
  // 4.0 is "onboarding 2.0" on main, tested on prod on 19 October. #3254 is
  // the last of its build before that.
  built: { n: '4.0', pr: 3254 },
  versions: [
    // The first major, whole pages each: 3.1 was drawn as 3.0 warmed up, not
    // as changes layered on it. On this page now rather than linked out to, so
    // switching to them keeps you on Screens like 4.0 and 4.1 do.
    {
      n: '3.0',
      note: "The first iteration: today's onboarding form, turned into a chat.",
      spec: noArrows({
        ...intakeProfileReviewSpec,
        title: 'Onboarding',
        links: [],
      }),
      changes: [
        "The form's questions, asked one at a time in a chat.",
        'A review step after it, to check what the AI picked up.',
      ],
    },
    {
      n: '3.1',
      note: "3.0 warmed up, after Steph's feedback. The same questions in the same order.",
      spec: noArrows({ ...onboarding11Spec, title: 'Onboarding', links: [] }),
      changes: [
        'Warmer words throughout, from the copy table in Warming up onboarding.',
        'Says what the answers are for, after the names.',
        "A number that matters, straight after who they're supporting.",
        'The care experts behind it, after the names.',
        'Consent, plain and honest.',
        'The wait shows the work.',
        'Keni named once, in the first message.',
      ],
    },
    {
      n: '4.0',
      note: 'Onboarding as one conversation, as built on dev.',
      changes: [
        'Opens on what they need help with, before who they are.',
        'Answered in their own words, talked or typed, then read straight away.',
        "The AI consent is Keni's first message, not two screens before the chat.",
        'Once the summary is confirmed, it only asks what the voice note left.',
        'Uses their name from their first answer on.',
      ],
      spec: noArrows(
        layer(onboarding20Spec, [{ remove: OPTION_B }], {
          title: 'Onboarding',
          links: [],
        }),
      ),
    },
    {
      n: '4.1',
      note: '4.0 with the changes from design review round 5.',
      ticket: 'ENG-3893',
      spec: noArrows(
        layer(
          onboarding20Spec,
          [
            { replace: 'Happy path', with: happy21 },
            ...[...GONE_IN_2_1, OPTION_B].map((title) => ({ remove: title })),
            {
              insertAfter: 'Happy path',
              flows: changed21.filter((f) => f.title !== WAY_OUT),
            },
          ],
          { title: 'Onboarding', links: [] },
        ),
      ),
      wrap: as21,
      changes: [
        "The opening doesn't ask for AI consent. It says what we do and that it takes about 3 minutes.",
        'Tell me more is answered in the chat, with how we use AI among it.',
        "Consent to share, or Power of Attorney, comes after the summary. It isn't asked if it's for themselves, and No carries on.",
        'After the summary, Keni says a few more things are left. No stepper.',
        "If they didn't say what they need help with, it's the last question, not straight after the summary.",
        'With the keyboard up, the app bar stays and the chat shrinks to fit.',
        'How we use AI, the AI policy and No AI consent are gone with the consent.',
      ],
    },
  ],
};

// The A/B test against the form, on its own page under the screens.
const abTest20: HandoverSpec = {
  id: 'onboarding-ab-test',
  title: 'A/B test',
  description: '',
  canvas: true,
  links: [],
  flows: [section(onboarding20Spec, OPTION_B)],
  states: [],
  components: {},
};

export const abTestVersions: VersionedSpec = {
  id: 'onboarding-ab-test',
  title: 'A/B test',
  versions: [
    {
      n: '4.0',
      note: 'Option B: what we do and the AI consent on their own screens, then the chat.',
      spec: abTest20,
    },
    {
      n: '4.1',
      note: '4.0 with a way out to the form.',
      spec: layer(abTest20, [
        {
          insertAfter: OPTION_B,
          // Open, and in a row of its own: it was hidden beside the other
          // changes, and here it is the point of the page.
          flows: [
            {
              ...section(onboarding21Spec, WAY_OUT),
              collapsed: false,
              beside: false,
            },
          ],
        },
      ]),
      wrap: as21,
      changes: [
        'A way out to the form, from Tell me more, for the B side of the test.',
      ],
    },
  ],
};

// The pieces each major's conversation is built from. Both are whole pages
// rather than layers: 4.0's patterns were drawn fresh, not as changes to 3.0's.
export const patternsVersions: VersionedSpec = {
  id: 'onboarding-patterns',
  title: 'Patterns',
  // 4.0's chat components, made the default for onboarding in #3250.
  built: { n: '4.0', pr: 3250 },
  versions: [
    {
      n: '3.0',
      note: 'The pieces onboarding 3 is built from.',
      spec: { ...patternsSpec, title: 'Patterns' },
    },
    {
      n: '4.0',
      note: "The pieces onboarding 4's conversation is built from.",
      spec: { ...patterns20Spec, title: 'Patterns' },
    },
    {
      n: '4.1',
      draft: true,
      note: 'When a question shows its answers, now the agent decides how they answer.',
      spec: patterns41Spec,
      changes: [
        'Answers attached only when seeing them helps: amounts in bands, or names they might not know.',
        'Yes or no, and things they already know, answered in their own words.',
        'What we hold, said back in the message when it is three things or fewer; a list in a card when it is more.',
        'More than one answer: tick each, then Done sends them together.',
      ],
    },
  ],
};
